import {
  Box,
  Divider,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import {
  faEdit,
  faPlus,
  faTrash,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonIcon,
  Checkbox,
  FormFeedback,
  Input,
  Modal,
  OutlineCard,
  TextArea,
  useToast
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { fetchJournalConfiguredChecklists } from 'src/api/queries';
import { useSaveJournalChecklists } from 'src/api/mutations';
import {
  IEditorialFormField,
  IWorkPhase
} from '@publishing-engine/domain/models';
import { Overlay } from 'src/components/Overlay';
import { FieldLabel } from 'src/components/Field';

// ---- Types ----

export interface CheckListFormModalProps {
  isOpen: boolean;
  journalId: string;
  articleWorkflowId: string;
  phase: IWorkPhase;
  phaseLabel: string;
  onClose: () => void;
}

interface ChecklistItem {
  name: string;
  main: string;
  description?: string;
  required: boolean;
  enabled: boolean;
}

interface ItemFormState {
  main: string;
  description: string;
  required: boolean;
}

type EditorMode = 'none' | 'create' | 'edit';

const EMPTY_FORM: ItemFormState = {
  main: '',
  description: '',
  required: false
};

const DESCRIPTION_MAX_LENGTH = 150;

// ---- Helpers ----

// Derive a stable camelCase field name from a human-readable label, e.g.
// "Manuscript Completeness" -> "manuscriptCompleteness".
const toCamelCase = (label: string): string =>
  label
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0
        ? lower
        : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');

// ---- Main component ----

const CheckListFormModal: React.FC<CheckListFormModalProps> = ({
  isOpen,
  journalId,
  articleWorkflowId,
  phase,
  phaseLabel,
  onClose
}) => {
  const toast = useToast();

  const saveMutation = useSaveJournalChecklists(journalId);

  // ---- Local state ----
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [editorMode, setEditorMode] = useState<EditorMode>('none');
  const [editingItemName, setEditingItemName] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ChecklistItem | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  // ---- Item form state (react-hook-form) ----
  const {
    control,
    register,
    handleSubmit,
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm<ItemFormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: EMPTY_FORM
  });

  // ---- Fetch configured checklists for this article workflow ----
  const { data: configuredData, isLoading: isConfiguredLoading } = useQuery({
    queryKey: [
      'journal-configuration-checklists',
      journalId,
      articleWorkflowId
    ],
    queryFn: () =>
      fetchJournalConfiguredChecklists(journalId, articleWorkflowId),
    enabled: isOpen && !!articleWorkflowId
  });

  const configuredSchema = useMemo(
    () => configuredData?.editorialFormSchemas?.find(s => s.phase === phase),
    [configuredData, phase]
  );

  const isDataLoading = isConfiguredLoading;

  // ---- Map configured fields into an editable list ----
  useEffect(() => {
    const configuredFields = configuredSchema?.formFields ?? [];

    const items: ChecklistItem[] = configuredFields.map(field => ({
      name: field.name,
      main: field.params.labels.main,
      description: field.params.labels.description,
      required: field.params.validations.required,
      enabled: true
    }));

    setItems(items);
    setEditorMode('none');
    setEditingItemName(null);
    reset(EMPTY_FORM);
    setItemToDelete(null);
    setDeleteAllOpen(false);
  }, [configuredData, configuredSchema, phase, reset]);

  // ---- Handlers ----

  // Persist the full phase checklist (enabled items only). The backend upserts
  // by (organizationId, phase, articleWorkflowId), so every action sends the
  // current list — no separate "save" step or schema id tracking is needed.
  const persistItems = useCallback(
    async (nextItems: ChecklistItem[], opts?: { silent?: boolean }) => {
      const enabledItems = nextItems.filter(item => item.enabled);

      const formFields: IEditorialFormField[] = enabledItems.map(item => ({
        name: item.name,
        component: 'Checkbox',
        params: {
          validations: { required: item.required },
          labels: {
            main: item.main,
            ...(item.description ? { description: item.description } : {})
          },
          caseDataField: `$.caseData.${item.name}`
        }
      }));

      const properties: Record<string, { type: 'boolean' }> = {};
      for (const item of enabledItems) {
        properties[item.name] = { type: 'boolean' };
      }

      const schema = {
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        additionalProperties: false,
        properties
      };

      const response = await saveMutation.mutateAsync([
        {
          phase,
          articleWorkflowId,
          formFields,
          schema
        }
      ]);

      if (response.isSuccess) {
        if (!opts?.silent) {
          toast({
            variant: 'success',
            title: 'Checklist updated',
            isClosable: true
          });
        }
        return true;
      }

      toast({
        variant: 'error',
        title:
          typeof response.message === 'string'
            ? response.message
            : 'Failed to save checklist',
        isClosable: true
      });
      return false;
    },
    [phase, articleWorkflowId, saveMutation, toast]
  );

  const openCreate = useCallback(() => {
    setEditingItemName(null);
    reset(EMPTY_FORM);
    setEditorMode('create');
  }, [reset]);

  const openEdit = useCallback(
    (item: ChecklistItem) => {
      setEditingItemName(item.name);
      reset({
        main: item.main,
        description: item.description ?? '',
        required: item.required
      });
      setEditorMode('edit');
    },
    [reset]
  );

  const handleCancelEdit = useCallback(() => {
    setEditorMode('none');
    setEditingItemName(null);
    reset(EMPTY_FORM);
  }, [reset]);

  // Save the item form. react-hook-form enforces the required-field rules; the
  // duplicate-label check runs here (create mode only) so it can compare against
  // the current item list.
  const handleSaveForm = handleSubmit(async () => {
    const { main, description, required } = getValues();
    const label = main.trim();
    const name = toCamelCase(label);

    if (editorMode === 'create') {
      if (!name) {
        setError('main', {
          type: 'manual',
          message: 'Label is required'
        });
        return;
      }

      const duplicate = items.some(
        item => item.main.trim().toLowerCase() === label.toLowerCase()
      );
      if (duplicate) {
        setError('main', {
          type: 'manual',
          message: 'An item with this label already exists.'
        });
        return;
      }
    }

    let next: ChecklistItem[];
    if (editorMode === 'create') {
      next = [
        ...items,
        {
          name,
          main: label,
          description: description.trim() || undefined,
          required,
          enabled: true
        }
      ];
    } else {
      next = items.map(item =>
        item.name === editingItemName
          ? {
              ...item,
              main: label,
              description: description.trim() || undefined,
              required
            }
          : item
      );
    }

    const ok = await persistItems(next);
    if (!ok) return;

    setItems(next);
    handleCancelEdit();
  });

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    const next = items.filter(item => item.name !== itemToDelete.name);
    const ok = await persistItems(next);
    if (!ok) return;

    setItems(next);
    setItemToDelete(null);
  }, [itemToDelete, items, persistItems]);

  const handleDeleteAll = useCallback(async () => {
    const ok = await persistItems([]);
    if (!ok) return;

    setItems([]);
    setDeleteAllOpen(false);
    setItemToDelete(null);
  }, [persistItems]);

  const handleClose = useCallback(() => {
    setEditorMode('none');
    setEditingItemName(null);
    reset(EMPTY_FORM);
    setItemToDelete(null);
    setDeleteAllOpen(false);
    onClose();
  }, [onClose, reset]);

  if (!isOpen) return null;

  return (
    <>
      <Overlay height="100%">
        <Flex
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
          minH="100vh"
        >
          <Flex
            direction="column"
            gap={6}
            mx={{ base: 4, md: 8 }}
            mt={{ base: 4, md: 8 }}
            paddingBottom="220px"
          >
            {/* ---- Header ---- */}
            <Flex alignItems="center" justifyContent="space-between" gap={3}>
              <Flex direction="column">
                <Text textStyle="headingSection">Checklist</Text>
                <Text textStyle="textBody">
                  Configure checklist items for{' '}
                  <Text as="span" fontWeight="semibold">
                    {phaseLabel}
                  </Text>
                </Text>
              </Flex>
              <Flex gap={2}>
                <Button
                  type="button"
                  variant="secondary"
                  iconLeft={<FontAwesomeIcon icon={faPlus} />}
                  onClick={openCreate}
                >
                  Add Checklist Item
                </Button>
                <Button
                  type="button"
                  variant="caution"
                  iconLeft={<FontAwesomeIcon icon={faTrash} />}
                  onClick={() => setDeleteAllOpen(true)}
                  isDisabled={items.length === 0}
                >
                  Delete All
                </Button>
              </Flex>
            </Flex>

            {/* ---- Loading spinner ---- */}
            {isDataLoading && (
              <Flex justify="center" align="center" paddingY={20}>
                <Spinner color="#1D4ED8" thickness="3px" />
              </Flex>
            )}

            {/* ---- Table ---- */}
            {!isDataLoading && (
              <OutlineCard borderless paddingX={4} paddingY={6}>
                {items.length === 0 ? (
                  <Flex
                    flexDirection="column"
                    gap={2}
                    alignItems="center"
                    paddingY={8}
                  >
                    <Text textStyle="headingGroup">
                      No checklist items configured
                    </Text>
                    <Text textStyle="textBody">
                      No checklist items exist for this phase. Add an item to
                      get started.
                    </Text>
                  </Flex>
                ) : (
                  <Box overflowX="auto" width="100%">
                    <Table>
                      <Thead>
                        <Tr
                          sx={{
                            '& th': {
                              textTransform: 'none',
                              color: 'black',
                              background: 'white'
                            }
                          }}
                        >
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Label
                            </Text>
                          </Th>
                          <Th p={4}>
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Description
                            </Text>
                          </Th>
                          <Th width="100px">
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Required
                            </Text>
                          </Th>
                          <Th width="120px" textAlign="center">
                            <Text textStyle="headingItem" whiteSpace="nowrap">
                              Actions
                            </Text>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {items.map(item => (
                          <Tr key={item.name} _last={{ borderBottom: 'none' }}>
                            <Td>
                              <Text textStyle="headingItem">{item.main}</Text>
                            </Td>
                            <Td maxWidth="320px" whiteSpace="normal">
                              <Text textStyle="textContext" wordBreak="break-word">
                                {item.description || '—'}
                              </Text>
                            </Td>
                            <Td>
                              <Text textStyle="textContext">
                                {item.required ? 'Yes' : 'No'}
                              </Text>
                            </Td>
                            <Td>
                              <Flex justifyContent="center" gap={2}>
                                <ButtonIcon
                                  variant="secondary"
                                  size="small"
                                  ariaLabel={`Edit ${item.main}`}
                                  icon={<FontAwesomeIcon icon={faEdit} />}
                                  onClick={() => openEdit(item)}
                                />
                                <ButtonIcon
                                  variant="caution"
                                  size="small"
                                  ariaLabel={`Delete ${item.main}`}
                                  icon={
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      style={{ color: 'red' }}
                                    />
                                  }
                                  onClick={() => setItemToDelete(item)}
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </OutlineCard>
            )}
          </Flex>
        </Flex>

        {/* ---- Sticky footer ---- */}
        <Flex
          padding={4}
          bottom={0}
          zIndex={1}
          justifyContent="flex-end"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
          width="100%"
        >
          <Flex gap={4}>
            <Button variant="caution" onClick={handleClose}>
              Exit
            </Button>
          </Flex>
        </Flex>
      </Overlay>

      {/* ---- Create / edit item modal ---- */}
      {editorMode !== 'none' && (
        <Modal
          isOpen={true}
          onClose={handleCancelEdit}
          width="45rem"
          closeButtonProps={{ hidden: true }}
        >
          <Flex flexDirection="column" gap={4} padding={8}>
            <Text textStyle="headingGroup">
              {editorMode === 'edit'
                ? 'Edit checklist item'
                : 'Add checklist item'}
            </Text>

            <Flex
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
              width="100%"
            >
              <FieldLabel label="Label" subText="" isRequired />
              <Input
                {...register('main', { required: 'Label is required' })}
                isInvalid={!!errors.main}
                placeholder="e.g. Manuscript Completeness"
                disabled={editorMode === 'edit'}
              />
              {errors.main && (
                <FormFeedback variant="error">
                  {errors.main.message}
                </FormFeedback>
              )}
            </Flex>

            <Flex
              flexDirection="column"
              alignItems="flex-start"
              gap={2}
              width="100%"
            >
              <FieldLabel label="Description" subText="" isRequired />
              <Controller
                name="description"
                control={control}
                rules={{
                  required: 'Description is required',
                  maxLength: {
                    value: DESCRIPTION_MAX_LENGTH,
                    message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`
                  }
                }}
                render={({ field }) => (
                  <TextArea
                    name={field.name}
                    value={field.value}
                    onChange={event => {
                      field.onChange(event);
                      if (event.target.value.length > DESCRIPTION_MAX_LENGTH) {
                        setError('description', {
                          type: 'maxLength',
                          message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`
                        });
                      } else if (errors.description) {
                        clearErrors('description');
                      }
                    }}
                    onBlur={event => {
                      field.onBlur();
                      field.onChange(event.target.value.trim());
                    }}
                    ref={field.ref}
                    isInvalid={!!errors.description}
                    placeholder="Describe what this item verifies"
                  />
                )}
              />
              {errors.description && (
                <FormFeedback variant="error">
                  {errors.description.message}
                </FormFeedback>
              )}
            </Flex>

            <Controller
              name="required"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="checklist-item-required"
                  name="checklist-item-required"
                  ariaLabel="Required"
                  isChecked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                >
                  Required
                </Checkbox>
              )}
            />

            <Divider />

            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveForm}
                isLoading={saveMutation.isPending}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}

      {/* ---- Delete confirmation modal ---- */}
      {itemToDelete && (
        <Modal
          isOpen={true}
          onClose={() => setItemToDelete(null)}
          width="448px"
          closeButtonProps={{ hidden: true }}
        >
          <Flex padding={8} gap={6} flexDirection="column">
            <Flex flexDirection="column" gap={2}>
              <Text
                textStyle="headingGroup"
                color="actionSecondaryCautionContent"
              >
                <FontAwesomeIcon icon={faWarning} /> Delete item?
              </Text>
              <Text textStyle="textBody">
                Are you sure you want to delete{' '}
                <Text as="span" fontWeight="semibold">
                  {itemToDelete.main}
                </Text>
                ? This action cannot be undone.
              </Text>
            </Flex>

            <Flex gap={2} justifyContent="flex-end">
              <Button variant="secondary" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button
                variant="caution"
                onClick={handleConfirmDelete}
                isLoading={saveMutation.isPending}
              >
                Delete
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}

      {/* ---- Delete all confirmation modal ---- */}
      {deleteAllOpen && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteAllOpen(false)}
          width="448px"
          closeButtonProps={{ hidden: true }}
        >
          <Flex padding={8} gap={6} flexDirection="column">
            <Flex flexDirection="column" gap={2}>
              <Text
                textStyle="headingGroup"
                color="actionSecondaryCautionContent"
              >
                <FontAwesomeIcon icon={faWarning} /> Delete all items?
              </Text>
              <Text textStyle="textBody">
                Are you sure you want to delete all checklist items for this
                phase? This action cannot be undone.
              </Text>
            </Flex>

            <Flex gap={2} justifyContent="flex-end">
              <Button
                variant="secondary"
                onClick={() => setDeleteAllOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="caution"
                onClick={handleDeleteAll}
                isLoading={saveMutation.isPending}
              >
                Delete All
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default CheckListFormModal;
